import type React from "react";

import type { BadgeVariant } from "@/components/badge";

import { Badge } from "@/components/badge";
import { Button } from "@/components/button";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
} from "@/components/dialog";
import { Modal } from "@/components/modal";

interface SelectedRequest {
  id: number;
  name: string;
}

interface BulkChangeStatusModalProps {
  isLoading?: boolean;
  isOpen: boolean;
  newStatusLabel: string;
  newStatusVariant: BadgeVariant;
  onConfirm: () => void;
  onOpenChange: React.Dispatch<React.SetStateAction<boolean>>;
  selectedRequests: SelectedRequest[];
}

export function BulkChangeStatusModal({
  isLoading = false,
  isOpen,
  newStatusLabel,
  newStatusVariant,
  onConfirm,
  onOpenChange,
  selectedRequests,
}: BulkChangeStatusModalProps) {
  const selectedCount = selectedRequests.length;
  const maxDisplayedRequests = 5;
  const displayedRequests = selectedRequests.slice(0, maxDisplayedRequests);
  const remainingCount = Math.max(0, selectedCount - maxDisplayedRequests);

  const handleConfirm = () => {
    onConfirm();
  };

  return (
    <Modal
      classNames={{ modal: "w-[500px]" }}
      isDismissable
      isOpen={isOpen}
      onOpenChange={onOpenChange}
    >
      <Dialog>
        <DialogHeader>
          <span className="flex gap-2">
            Change {selectedCount} request{selectedCount === 1 ? "" : "s"} to{" "}
            <Badge
              className="ml-1"
              isRounded
              size="small"
              variant={newStatusVariant}
            >
              {newStatusLabel}
            </Badge>
          </span>
        </DialogHeader>
        <DialogBody>
          <div className="flex flex-col gap-xs">
            <DialogCloseButton />
          </div>
          <div className="mt-4">
            <p className="text-sm font-medium text-text-primary mb-3">
              Request titles:
            </p>
            <ul className="list-disc list-inside space-y-1.5">
              {displayedRequests.map((request) => (
                <li
                  className="text-sm text-text-secondary truncate"
                  key={request.id}
                >
                  {request.name}
                </li>
              ))}
            </ul>
            {remainingCount > 0 && (
              <p className="text-sm text-text-tertiary mt-2 italic">
                ...and {remainingCount} more request
                {remainingCount === 1 ? "" : "s"}
              </p>
            )}
          </div>
        </DialogBody>
        <DialogFooter className="flex flex-grow-1 mt-lg">
          <Button
            className="flex-1"
            color="gray"
            onPress={() => onOpenChange(false)}
            size="lg"
            variant="secondary"
          >
            Cancel
          </Button>
          <Button
            className="flex-1"
            color="color"
            isLoading={isLoading}
            onPress={handleConfirm}
            size="lg"
            variant="primary"
          >
            Confirm
          </Button>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}
